'use client';

import React, { useState, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { add, isEmpty, values } from 'lodash';
import { useForm, FormProvider } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';

import { buildInitialFormState } from './buildInitialFormState';
import { fields } from './fields';
import { RichText } from '@/components/RichText';
import { Checkbox as CheckboxUi } from '@/components/ui/checkbox';
import { getClientSideURL } from '@/utilities/getURL';
import { cn } from '@/utilities/cn';
import { NAV } from '@/constants';
import type { Form as FormType } from '@payload-types';

export type Value = unknown;

export interface Property {
  [key: string]: Value;
}

export interface Data {
  [key: string]: Property | Property[];
}

export const FormBlock: React.FC<{
  form: FormType;
  additionalSubmissionData?: {
    field: string;
    value: string;
  }[];
  submitBtnClassName?: string;
  submitBtnText?: string;
  buttonVariant?: string;
  theme?: 'default' | 'secondary';
  callback?: () => any;
}> = ({
  form,
  additionalSubmissionData = [],
  submitBtnClassName,
  submitBtnText,
  buttonVariant,
  theme = 'default',
  callback,
}) => {
  const [policyAccepted, setPolicy] = useState(true);

  const {
    id,
    fields: fieldDataList,
    confirmationMessage,
    confirmationType,
    redirect,
    submitButtonLabel = 'Отправить',
  } = form;

  const formID = String(id);

  const formMethods = useForm({
    defaultValues: buildInitialFormState(fieldDataList),
  });
  const {
    control,
    handleSubmit,
    register,
    setError,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = formMethods;

  const router = useRouter();
  const pathname = usePathname();

  const onSubmit = useCallback(
    (data: Data) => {
      const submitForm = async () => {
        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }));

        try {
          const req = await fetch(
            `${getClientSideURL()}/api/form-submissions`,
            {
              body: JSON.stringify({
                form: formID,
                submissionData: [
                  ...dataToSend,
                  {
                    field: 'URL страницы',
                    value: `${getClientSideURL()}${pathname}`,
                  },
                  ...additionalSubmissionData,
                ],
              }),
              headers: {
                'Content-Type': 'application/json',
              },
              method: 'POST',
            },
          );

          const res = await req.json();

          if (req.status >= 400) {
            setError('root', {
              message: res.errors?.[0]?.message || 'Ошибка на сервере',
            });
            return;
          }

          reset();

          if (callback) callback();
          toast.success(
            confirmationMessage ? (
              <RichText data={confirmationMessage} />
            ) : (
              'Заявка успешно отправлена'
            ),
            { duration: 5000 },
          );

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect;

            const redirectUrl = url;

            if (redirectUrl) router.push(redirectUrl);
          }
        } catch (err) {
          console.warn(err);
          setError('root', {
            message: err?.message || 'Ошибка на сервере',
          });
        }
      };

      void submitForm();
    },
    [
      router,
      formID,
      redirect,
      confirmationType,
      additionalSubmissionData,
      pathname,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      {!isEmpty(errors) && <div>{values(errors as any)?.[0]?.message}</div>}

      <form id={formID} onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3 md:gap-2.5">
          {fieldDataList?.map((field, index) => {
            const Field: React.FC<any> = fields?.[field.blockType];
            if (Field) {
              return (
                <div key={index}>
                  <Field
                    className={cn(theme === 'secondary' && 'bg-[#F2F3F5]')}
                    form={form}
                    {...field}
                    {...formMethods}
                    control={control}
                    errors={errors}
                    register={register}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>

        <input name="pageUrl" value={pathname} type="hidden" />

        <label className="mt-4 flex gap-2">
          <CheckboxUi
            onClick={(e) => setPolicy((p) => !p)}
            value={String(policyAccepted)}
            checked={policyAccepted}
            variant={theme}
          />

          <div className="pt-[2px] text-ps opacity-50">
            Нажимая на кнопку, Вы принимаете{' '}
            <Link href={NAV.privacyPolicy}>
              Положение и Согласие на обработку персональных данных
            </Link>
          </div>
        </label>

        <Button
          className={cn('mt-10 md:mt-[30px] md:w-full', submitBtnClassName)}
          // form={formID}
          disabled={isSubmitting || !policyAccepted}
          loading={isSubmitting}
          type="submit"
          variant={buttonVariant as any}
        >
          {submitBtnText || submitButtonLabel}
        </Button>
      </form>
    </FormProvider>
  );
};
